import React, { useEffect, useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import LoadingAnimation from '../components/loadingAnimation';
import SvgGauge from '../components/SvgGauge';
import { fetchDiskInventory, subscribeDiskEvents, refreshDiskInstance } from '../utils/diskApi';
import { useDataCache } from '../hooks/useDataCache';
import { cacheManager } from '../hooks/GlobalCacheManager';
import { EntityCellRenderer } from '../utils/metrics';
import { getApiBase } from '../utils/apiBase';
import RadioTabs from '../components/common/RadioTabs';
import SelectField from '../components/common/SelectField';
import '../styles/diskUsage.css';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

function formatBytes(n) {
  try {
    const v = Number(n || 0);
    if (!Number.isFinite(v) || v <= 0) return '0B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    let s = v;
    let i = 0;
    while (s >= 1024 && i < units.length - 1) {
      s /= 1024;
      i++;
    }
    const num = s >= 100 ? Math.round(s) : s >= 10 ? s.toFixed(1) : s.toFixed(2);
    return `${num}${units[i]}`;
  } catch {
    return '0B';
  }
}

function formatDiskLabel(value) {
  if (value == null) return '';
  const raw = String(value);
  if (raw === '/' || raw === '') return 'MAIN';
  const trimmed = raw.replace(/\/+$/, '');
  const parts = trimmed.split('/');
  const last = parts[parts.length - 1] || raw;
  return last || raw;
}

function computeDiskSummaryMetrics(inst) {
  try {
    const mountName = inst?.dockerReport?.filesystem?.mount || inst?.diskSpace?.mountpoint || null;
    const currentMount = mountName || '/';
    const hasFsBreakdown = !!(
      inst?.dockerReport?.filesystem?.breakdown &&
      Object.keys(inst.dockerReport.filesystem.breakdown || {}).length > 0
    );
    const primaryMount = (() => {
      try {
        const arr = inst?.dockerReport?.filesystems;
        return Array.isArray(arr) && arr.length > 0 ? arr[0].mount : currentMount;
      } catch {
        return currentMount;
      }
    })();
    const isPrimaryMount = String(currentMount) === String(primaryMount);
    const b = inst?.dockerReport?.filesystem?.breakdown || inst?.breakdown || null;

    const containersRwAligned = hasFsBreakdown
      ? Number((b?.containersRwBytes ?? b?.containers_rw_bytes) ?? 0)
      : 0;
    const imagesActiveAligned = hasFsBreakdown
      ? Number((b?.activeImagesBytes ?? b?.images_active_bytes) ?? 0)
      : isPrimaryMount
        ? Number(inst?.dockerReport?.summary?.images_active_bytes || 0)
        : 0;
    const databasesActiveVolumesAligned = hasFsBreakdown
      ? Number((b?.activeVolumesBytes ?? b?.active_volumes_bytes) ?? 0)
      : 0;
    const reclaimableAligned = hasFsBreakdown
      ? (Number((b?.reclaimableImagesBytes ?? b?.reclaimable_images_bytes) ?? 0) +
         Number((b?.reclaimableContainersBytes ?? b?.reclaimable_containers_bytes) ?? 0) +
         Number((b?.reclaimableVolumesBytes ?? b?.reclaimable_volumes_bytes) ?? 0))
      : 0;
    const usedBytesExact = Number(inst?.dockerReport?.filesystem?.used_bytes || 0) || 0;

    const dbAssignedHereBytes = (() => {
      try {
        const doc = inst?.dockerReport;
        const fsArr = Array.isArray(doc?.filesystems) ? doc.filesystems : [];
        const detail = Array.isArray(doc?.databases_detail) ? doc.databases_detail : [];
        if (!detail || detail.length === 0 || !fsArr || fsArr.length === 0) return 0;
        const candidates = fsArr.map(fs => ({
          mount: String(fs?.mount || ''),
          used: Number(fs?.used_bytes || 0) || 0,
          size: Number(fs?.size_bytes || 0) || 0,
          activeVol: Number((fs?.breakdown?.active_volumes_bytes) || 0) || 0
        }));
        const tinyCutoff = 2 * 1024 * 1024 * 1024;
        const assignRows = (rows) => {
          const out = [];
          const sorted = rows
            .slice()
            .sort((a, b) => (Number(b.bytes || 0) || 0) - (Number(a.bytes || 0) || 0));
          for (const r of sorted) {
            const v = Number(r.bytes || 0) || 0;
            if (v <= 0) {
              out.push({ ...r, mount: primaryMount });
              continue;
            }
            const withActive = candidates.filter(c => c.activeVol > 0);
            const tolA = Math.max(v * 0.2, 10 * 1024 * 1024 * 1024);
            const close = withActive.filter(
              c => Math.abs((c.activeVol || 0) - v) <= tolA
            );
            let chosen = null;
            const pickMinBy = (arr, key) =>
              arr.reduce((best, c) => {
                const db = Math.abs((best[key] || 0) - v);
                const dc = Math.abs((c[key] || 0) - v);
                return dc < db ? c : best;
              }, arr[0]);
            if (close.length > 0) {
              chosen = pickMinBy(close, 'activeVol');
            }
            if (!chosen) {
              const covers = candidates.filter(
                c => c.used >= v * 0.5 && c.size > tinyCutoff
              );
              if (covers.length > 0) {
                chosen = pickMinBy(covers, 'used');
              }
            }
            if (!chosen) {
              const avail = candidates.filter(c => c.size > tinyCutoff);
              chosen = avail[0] || candidates[0] || { mount: primaryMount };
            }
            out.push({ ...r, mount: chosen.mount });
          }
          return out;
        };
        const assigned = assignRows(
          detail.map(e => ({ bytes: Number(e?.bytes || 0) || 0 }))
        );
        const sum = assigned.reduce(
          (acc, r) =>
            acc +
            (String(r.mount) === String(currentMount)
              ? Number(r.bytes || 0) || 0
              : 0),
          0
        );
        return sum;
      } catch {
        return 0;
      }
    })();

    const filteredContainers = (() => {
      try {
        const arr = Array.isArray(inst?.dockerReport?.containers)
          ? inst.dockerReport.containers
          : [];
        const canFilter = arr.some(c => c && (c.upper_mount || c.log_mount));
        let out = canFilter
          ? arr.filter(
              c =>
                String(c?.upper_mount || '') === String(currentMount) ||
                String(c?.log_mount || '') === String(currentMount)
            )
          : arr;
        if (out.length === 0 && isPrimaryMount) out = arr;
        return out;
      } catch {
        return [];
      }
    })();

    const runningLogsBytes = (() => {
      try {
        return filteredContainers
          .filter(c => !!c && c.is_running)
          .reduce(
            (a, c) => a + (Number(c.log_bytes || 0) || 0),
            0
          );
      } catch {
        return 0;
      }
    })();

    const runningTotalsBytes = (() => {
      try {
        return filteredContainers
          .filter(c => !!c && c.is_running)
          .reduce(
            (a, c) => a + (Number(c.total_bytes || 0) || 0),
            0
          );
      } catch {
        return 0;
      }
    })();

    const runningRwBytes = (() => {
      try {
        return filteredContainers
          .filter(c => !!c && c.is_running)
          .reduce(
            (a, c) => a + (Number(c.rw_bytes || 0) || 0),
            0
          );
      } catch {
        return 0;
      }
    })();

    const allTotalsBytes = (() => {
      try {
        const arr = filteredContainers;
        return arr.reduce(
          (a, c) => a + (Number(c.total_bytes || 0) || 0),
          0
        );
      } catch {
        return 0;
      }
    })();

    let containersDisplayBytes = Math.max(
      0,
      Number(containersRwAligned || 0) + Number(runningLogsBytes || 0)
    );
    if (containersDisplayBytes === 0) {
      const rwPlusLogs = Math.max(
        0,
        Number(runningRwBytes || 0) + Number(runningLogsBytes || 0)
      );
      if (rwPlusLogs > 0) containersDisplayBytes = rwPlusLogs;
    }
    if (containersDisplayBytes === 0 && isPrimaryMount) {
      const summaryActive = Number(
        inst?.dockerReport?.summary?.containers_rw_active_bytes || 0
      );
      const summaryLogs = Number(
        inst?.dockerReport?.summary?.logs_total_bytes || 0
      );
      const activePlusLogs = Math.max(0, summaryActive + summaryLogs);
      if (activePlusLogs > 0) containersDisplayBytes = activePlusLogs;
    }
    if (containersDisplayBytes === 0 && allTotalsBytes > 0) {
      containersDisplayBytes = allTotalsBytes;
    }

    let imagesDisplayBytes = Number(imagesActiveAligned || 0);
    if (!hasFsBreakdown && isPrimaryMount) {
      const sum = Number(inst?.dockerReport?.summary?.images_total_bytes || 0);
      if (!imagesDisplayBytes || imagesDisplayBytes === 0) {
        imagesDisplayBytes = sum;
        if (
          (!imagesDisplayBytes || imagesDisplayBytes === 0) &&
          Array.isArray(inst?.dockerReport?.images)
        ) {
          imagesDisplayBytes = inst.dockerReport.images.reduce(
            (a, img) => a + (Number(img?.size_bytes || 0) || 0),
            0
          );
        }
      }
    }

    const dbBest = Math.max(
      Number(databasesActiveVolumesAligned || 0),
      Number(dbAssignedHereBytes || 0)
    );
    let dbDisplayBytes = Math.min(
      Math.max(0, dbBest),
      Math.max(
        0,
        usedBytesExact -
          (Number(containersDisplayBytes || 0) +
            Number(imagesDisplayBytes || 0) +
            Number(reclaimableAligned || 0))
      )
    );

    const otherDisplayBytes = Math.max(
      0,
      usedBytesExact -
        (Number(containersDisplayBytes || 0) +
          Number(imagesDisplayBytes || 0) +
          Number(dbDisplayBytes || 0) +
          Number(reclaimableAligned || 0))
    );

    return {
      containersDisplayBytes,
      imagesDisplayBytes,
      dbDisplayBytes,
      otherDisplayBytes,
      reclaimableAligned
    };
  } catch {
    return {
      containersDisplayBytes: 0,
      imagesDisplayBytes: 0,
      dbDisplayBytes: 0,
      otherDisplayBytes: 0,
      reclaimableAligned: 0
    };
  }
}

function InstanceCard({ inst, index, SmoothGauge, isExpanded, onToggleExpand, nodeDbInfo, dbSizes, limited = false }) {
  
  const fsSizeB = Number(inst?.dockerReport?.filesystem?.size_bytes || 0) || 0;
  const fsUsedB = Number(inst?.dockerReport?.filesystem?.used_bytes || 0) || 0;
  const usage = fsSizeB > 0 ? Math.max(0, Math.min(100, Math.round((fsUsedB / fsSizeB) * 100))) : parseInt(String(inst?.diskSpace?.usePercent || '0').replace('%','') || '0', 10);
  const used = inst?.diskSpace?.used || '-';
  const size = inst?.diskSpace?.size || '-';
  const available = inst?.diskSpace?.available || '-';
  const updated = inst?.lastUpdated ? new Date(inst.lastUpdated).toLocaleString() : '';
  const containers = Array.isArray(inst?.containers) ? inst.containers.slice().sort((a,b) => (b.rwSizeBytes||0)-(a.rwSizeBytes||0)) : [];
  const mountName = inst?.dockerReport?.filesystem?.mount || inst?.diskSpace?.mountpoint || null;
  const currentMount = mountName || '/';
  const hasFsBreakdown = !!(inst?.dockerReport?.filesystem?.breakdown && Object.keys(inst.dockerReport.filesystem.breakdown || {}).length > 0);
  const primaryMount = (() => { try { const arr = inst?.dockerReport?.filesystems; return Array.isArray(arr) && arr.length > 0 ? arr[0].mount : currentMount; } catch { return currentMount; } })();
  const isPrimaryMount = String(currentMount) === String(primaryMount);
  const b = inst?.dockerReport?.filesystem?.breakdown || inst?.breakdown || null;
  // Aligned metrics (tiles must sum to Used without double counting)
  const containersRwAligned = hasFsBreakdown
    ? Number((b?.containersRwBytes ?? b?.containers_rw_bytes) ?? 0)
    : 0;
  const imagesActiveAligned = hasFsBreakdown
    ? Number((b?.activeImagesBytes ?? b?.images_active_bytes) ?? 0)
    : (isPrimaryMount ? Number(inst?.dockerReport?.summary?.images_active_bytes || 0) : 0);
  const databasesActiveVolumesAligned = hasFsBreakdown
    ? Number((b?.activeVolumesBytes ?? b?.active_volumes_bytes) ?? 0)
    : 0;
  const reclaimableAligned = hasFsBreakdown
    ? (Number((b?.reclaimableImagesBytes ?? b?.reclaimable_images_bytes) ?? 0)
      + Number((b?.reclaimableContainersBytes ?? b?.reclaimable_containers_bytes) ?? 0)
      + Number((b?.reclaimableVolumesBytes ?? b?.reclaimable_volumes_bytes) ?? 0))
    : 0;
  const otherAligned = hasFsBreakdown
    ? Number((b?.otherBytes ?? b?.other_bytes) ?? 0)
    : 0;
  const usedBytesExact = Number(inst?.dockerReport?.filesystem?.used_bytes || 0) || 0;
  // Robust DB attribution when breakdown is missing: map each DB row to a filesystem mount.
  const dbAssignedHereBytes = (() => {
    try {
      const doc = inst?.dockerReport;
      const fsArr = Array.isArray(doc?.filesystems) ? doc.filesystems : [];
      const detail = Array.isArray(doc?.databases_detail) ? doc.databases_detail : [];
      if (!detail || detail.length === 0 || !fsArr || fsArr.length === 0) return 0;
      const primary = primaryMount;
      // Precompute candidates with deep active volumes when available
      const candidates = fsArr.map(fs => ({
        mount: String(fs?.mount || ''),
        used: Number(fs?.used_bytes || 0) || 0,
        size: Number(fs?.size_bytes || 0) || 0,
        activeVol: Number((fs?.breakdown?.active_volumes_bytes) || 0) || 0
      }));
      const tinyCutoff = 2 * 1024 * 1024 * 1024; // 2GB, exclude tiny mounts from attribution
      const assignRows = (rows) => {
        const out = [];
        const sorted = rows.slice().sort((a, b) => (Number(b.bytes||0)||0) - (Number(a.bytes||0)||0));
        for (const r of sorted) {
          const v = Number(r.bytes || 0) || 0;
          if (v <= 0) { out.push({ ...r, mount: primary }); continue; }
          // 1) Prefer mounts with active volumes close to this row size
          const withActive = candidates.filter(c => c.activeVol > 0);
          const tolA = Math.max(v * 0.20, 10 * 1024 * 1024 * 1024); // 20% or 10GB
          const close = withActive.filter(c => Math.abs((c.activeVol || 0) - v) <= tolA);
          let chosen = null;
          const pickMinBy = (arr, key) => arr.reduce((best, c) => {
            const db = Math.abs((best[key] || 0) - v);
            const dc = Math.abs((c[key] || 0) - v);
            return dc < db ? c : best;
          }, arr[0]);
          if (close.length > 0) {
            chosen = pickMinBy(close, 'activeVol');
          }
          // 2) Fallback: among non-tiny mounts, pick one whose used covers at least half
          if (!chosen) {
            const covers = candidates.filter(c => c.used >= v * 0.5 && c.size > tinyCutoff);
            if (covers.length > 0) {
              chosen = pickMinBy(covers, 'used');
            }
          }
          // 3) Last resort: any non-tiny mount, else primary
          if (!chosen) {
            const avail = candidates.filter(c => c.size > tinyCutoff);
            chosen = avail[0] || candidates[0] || { mount: primary };
          }
          out.push({ ...r, mount: chosen.mount });
        }
        return out;
      };
      const assigned = assignRows(detail.map(e => ({ bytes: Number(e?.bytes || 0) || 0 })));
      const sum = assigned.reduce((acc, r) => acc + (String(r.mount) === String(currentMount) ? Number(r.bytes||0)||0 : 0), 0);
      return sum;
    } catch { return 0; }
  })();
  const filteredContainers = (() => {
    try {
      const arr = Array.isArray(inst?.dockerReport?.containers) ? inst.dockerReport.containers : [];
      const canFilter = arr.some(c => c && (c.upper_mount || c.log_mount));
      let out = canFilter
        ? arr.filter(c => (String(c?.upper_mount || '') === String(currentMount)) || (String(c?.log_mount || '') === String(currentMount)))
        : arr;
      if (out.length === 0 && isPrimaryMount) out = arr;
      return out;
    } catch { return []; }
  })();
  const runningLogsBytes = (() => {
    try {
      return filteredContainers.filter(c => !!c && c.is_running).reduce((a, c) => a + (Number(c.log_bytes || 0) || 0), 0);
    } catch { return 0; }
  })();
  const runningTotalsBytes = (() => {
    try {
      return filteredContainers.filter(c => !!c && c.is_running).reduce((a, c) => a + (Number(c.total_bytes || 0) || 0), 0);
    } catch { return 0; }
  })();
  const runningRwBytes = (() => {
    try {
      return filteredContainers.filter(c => !!c && c.is_running).reduce((a, c) => a + (Number(c.rw_bytes || 0) || 0), 0);
    } catch { return 0; }
  })();
  const allTotalsBytes = (() => {
    try {
      const arr = filteredContainers;
      return arr.reduce((a, c) => a + (Number(c.total_bytes || 0) || 0), 0);
    } catch { return 0; }
  })();
  let containersDisplayBytes = Math.max(0, Number(containersRwAligned || 0) + Number(runningLogsBytes || 0));
  if (containersDisplayBytes === 0) {
    const rwPlusLogs = Math.max(0, Number(runningRwBytes || 0) + Number(runningLogsBytes || 0));
    if (rwPlusLogs > 0) containersDisplayBytes = rwPlusLogs;
  }
  if (containersDisplayBytes === 0 && isPrimaryMount) {
    const summaryActive = Number(inst?.dockerReport?.summary?.containers_rw_active_bytes || 0);
    const summaryLogs = Number(inst?.dockerReport?.summary?.logs_total_bytes || 0);
    const activePlusLogs = Math.max(0, summaryActive + summaryLogs);
    if (activePlusLogs > 0) containersDisplayBytes = activePlusLogs;
  }
  if (containersDisplayBytes === 0 && allTotalsBytes > 0) {
    containersDisplayBytes = allTotalsBytes;
  }
  // Images display: prefer per-fs active when available; otherwise on primary use summary total as fallback
  let imagesDisplayBytes = Number(imagesActiveAligned || 0);
  if (!hasFsBreakdown && isPrimaryMount) {
    const sum = Number(inst?.dockerReport?.summary?.images_total_bytes || 0);
    if (!imagesDisplayBytes || imagesDisplayBytes === 0) {
      imagesDisplayBytes = sum;
      if ((!imagesDisplayBytes || imagesDisplayBytes === 0) && Array.isArray(inst?.dockerReport?.images)) {
        imagesDisplayBytes = inst.dockerReport.images.reduce((a, img) => a + (Number(img?.size_bytes || 0) || 0), 0);
      }
    }
  }

  const dbBest = Math.max(Number(databasesActiveVolumesAligned||0), Number(dbAssignedHereBytes||0));
  let dbDisplayBytes = Math.min(
    Math.max(0, dbBest),
    Math.max(0, usedBytesExact - (Number(containersDisplayBytes||0) + Number(imagesDisplayBytes||0) + Number(reclaimableAligned||0)))
  );
  const otherDisplayBytes = Math.max(0, usedBytesExact - (Number(containersDisplayBytes||0) + Number(imagesDisplayBytes||0) + Number(dbDisplayBytes||0) + Number(reclaimableAligned||0)));
  const dbTotalBytes = (() => {
    try {
      const doc = inst?.dockerReport;
      if (Array.isArray(doc?.databases_detail) && doc.databases_detail.length > 0) {
        return doc.databases_detail.reduce((a, e) => a + (Number(e && e.bytes || 0) || 0), 0);
      }
      if (doc && doc.databases && typeof doc.databases === 'object') {
        return Object.values(doc.databases).reduce((a, v) => a + (Number(v || 0) || 0), 0);
      }
    } catch {}
    return 0;
  })();
  const reclaimableTotalAll = (() => {
    try {
      const r = inst?.dockerReport?.reclaimable;
      if (!r || typeof r !== 'object') return 0;
      const t = Number(r.total_bytes || 0);
      if (t > 0) return t;
      const img = Number(r.images?.dangling?.bytes || 0);
      const ctr = Number(r.containers?.stopped?.total_bytes || 0);
      const vol = Number(r.volumes?.unused?.bytes || 0);
      return img + ctr + vol;
    } catch { return 0; }
  })();
  const showReclaimable = isPrimaryMount && reclaimableTotalAll > 0;
  const f = (n) => {
    try {
      const v = Number(n || 0);
      if (!Number.isFinite(v) || v <= 0) return '0B';
      const units = ['B','KB','MB','GB','TB','PB'];
      let s = v; let i = 0;
      while (s >= 1024 && i < units.length - 1) { s /= 1024; i++; }
      const num = s >= 100 ? Math.round(s) : (s >= 10 ? s.toFixed(1) : s.toFixed(2));
      return `${num}${units[i]}`;
    } catch { return '0B'; }
  };

  // Extract entity from filename pattern or use instance entity
  const getEntityFromInstance = () => {
    // Try to get entity from instance data first
    if (inst.entity) return inst.entity;
    
    // Fallback: derive from instanceId or host
    const instanceId = inst.instanceId || '';
    const host = inst.host || '';
    
    if (instanceId.includes('env') || host.includes('env')) return 'ENV';
    if (instanceId.includes('uys') || host.includes('uys')) return 'UYS';
    
    return 'ENV'; // default fallback
  };

  const entity = getEntityFromInstance();
  const gaugeId = `disk-gauge-${inst.instanceId || 'unknown'}-${inst.host || 'unknown'}-${index}`;

  return (
    <div className="disk-card">
      {/* Header - Collapsed View */}
      <div 
        className="disk-card__header"
        onClick={onToggleExpand}
      >
        <div className="disk-card__head-left">
          <div className="disk-card__brand">
            <EntityCellRenderer value={entity} size={32} showText={false} />
            <div className="disk-card__brand-text">
              <div className="disk-card__brand-entity">{inst.instanceId}</div>
              <div className="disk-card__brand-host">{inst.host}{mountName ? ` — ${mountName}` : ''}</div>
            </div>
          </div>
        </div>
        <img
          src="https://cdn.prod.website-files.com/64eb6710f68f6b333f8886a6/671a2db89cdd4d25e9d6855a_dropdown.svg"
          alt="Toggle"
          className={`dropdown-icon ${isExpanded ? 'open' : ''}`}
        />
      </div>

      {/* Summary Section - Always Visible */}
      <div className="summary-grid">
        <div className="gauge-wrap">
          <SmoothGauge id={gaugeId} value={usage} />
        </div>
          <div className="top-stats-grid">
            <div className="stat-tile">
              <div className="stat-label">Size</div>
              <div className="stat-value">{f(inst?.dockerReport?.filesystem?.size_bytes || size)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Used</div>
              <div className="stat-value">{f(inst?.dockerReport?.filesystem?.used_bytes || used)}</div>
            </div>
            <div className="stat-tile">
              <div className="stat-label">Available</div>
              <div className="stat-value">{f(inst?.dockerReport?.filesystem?.avail_bytes || available)}</div>
            </div>
          </div>
          <div className="updated-line">Updated {updated}</div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="expanded">
          {inst?.dockerReport ? (
            <>
              {/* Summary */}
              <div>
                <div className="section-panel">
                  <div className="panel-header">Summary</div>
                  <div className="stats-grid">
                    <div className="stat-tile">
                      <div className="stat-label">CONTAINERS</div>
                      <div className="stat-value">{f(containersDisplayBytes)}</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-label">IMAGES</div>
                      <div className="stat-value">{f(imagesDisplayBytes)}</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-label">DATABASES</div>
                      <div className="stat-value">{f(dbDisplayBytes)}</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-label">RECLAIMABLE</div>
                      <div className="stat-value">{f(reclaimableAligned)}</div>
                    </div>
                    <div className="stat-tile">
                      <div className="stat-label">OTHER</div>
                      <div className="stat-value">{f(otherDisplayBytes)}</div>
                    </div>
                  </div>
                  {(() => {
                    try {
                      const usedBytes = usedBytesExact;
                      const sumBytes = Number(containersDisplayBytes || 0) + Number(imagesActiveAligned || 0) + Number(dbDisplayBytes || 0) + Number(reclaimableAligned || 0) + Number(otherDisplayBytes || 0);
                      const delta = usedBytes - sumBytes;
                      const absDelta = Math.abs(delta);
                      const threshold = Math.max(usedBytes * 0.01, 2 * 1024 * 1024 * 1024); // 1% or 2GB
                      if (absDelta > threshold) {
                        return (
                          <div className="text-muted" style={{ marginTop: 6 }}>
                            Tiles delta {f(absDelta)} {delta < 0 ? '(over)' : '(under)'} Used.
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}
                  {(() => {
                    try {
                      const dbMissing = Math.max(0, Number(databasesActiveVolumesAligned || 0) - Number(dbDisplayBytes || 0));
                      // Show notice if > 1GB appears to be outside the reported filesystem
                      if (dbMissing > 1 * 1024 * 1024 * 1024) {
                        return (
                          <div className="text-muted" style={{ marginTop: 4 }}>
                            Additional DB data on other mounts: {f(dbMissing)} not shown in Size/Used.
                          </div>
                        );
                      }
                    } catch {}
                    return null;
                  })()}
                </div>
              </div>

              {/* Containers */}
              {(() => {
                // Show running containers only so the section sum matches the tile (which uses running RW + logs)
                const arr = filteredContainers.filter(c => !!c && c.is_running);
                if (arr.length > 0) {
                  return (
                    <div className="section-panel">
                      <div className="panel-header">Containers</div>
                      <div className="row-list">
                        {arr.map(c => (
                          <div key={`${inst.instanceId}-${c.id}`} className="row-tile">
                            <div className="container-row">
                              <div className="container-row-left">
                                <div className="td container-name">{c.name}</div>
                                <div className="td container-meta">
                                  <span className="kv-value">{f(c.total_bytes)}</span>
                                </div>
                              </div>
                              <div className="container-row-right">
                                <span className={`pill ${c.is_running ? 'pill--success' : 'pill--muted'}`}>{c.is_running ? 'Running' : 'Stopped'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Images */}
              {(() => {
                const imgActive = Number((b?.images_active_bytes ?? b?.activeImagesBytes) ?? 0);
                const showImages = hasFsBreakdown ? (imgActive > 0) : isPrimaryMount;
                const useActiveOnly = hasFsBreakdown; // tile uses active images when breakdown exists
                if (showImages && Array.isArray(inst.dockerReport?.images) && inst.dockerReport.images.length > 0) {
                  const list = useActiveOnly ? inst.dockerReport.images.filter(img => !!img && img.is_active) : inst.dockerReport.images;
                  return (
                    <div className="section-panel">
                      <div className="panel-header">Images</div>
                      <div className="row-list">
                        {list.map(img => (
                          <div key={`${inst.instanceId}-${img.image_id}`} className="row-tile">
                            <div className="container-row">
                              <div className="container-row-left">
                                <div className="td container-name">{img.repo_tag}</div>
                                <div className="td container-meta">
                                  <span className="kv-value">{f(img.size_bytes)}</span>
                                </div>
                              </div>
                              <div className="container-row-right">
                                <span className={`pill ${img.is_active ? 'pill--success' : 'pill--muted'}`}>{img.is_active ? 'Active' : 'Inactive'}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}

              {/* Reclaimable */}
              {showReclaimable && (
              <div>
                <div className="section-panel">
                  <div className="panel-header">Reclaimable</div>
                  <div className="row-list">
                    <div className="row-tile">
                      <div className="container-row">
                        <div className="container-row-left">
                          <div className="td container-name">Images (dangling)</div>
                          <div className="td container-meta">
                            <span className="kv-value">{f(inst.dockerReport?.reclaimable?.images?.dangling?.bytes)}</span>
                          </div>
                        </div>
                        <div className="container-row-right">
                          {(() => { const c = inst.dockerReport?.reclaimable?.images?.dangling?.count ?? 0; return (
                            <span className={`pill ${c > 0 ? 'pill--info' : 'pill--muted'}`}>{c}</span>
                          ); })()}
                        </div>
                      </div>
                    </div>
                    <div className="row-tile">
                      <div className="container-row">
                        <div className="container-row-left">
                          <div className="td container-name">Containers (stopped)</div>
                          <div className="td container-meta">
                            <span className="kv-value">{f(inst.dockerReport?.reclaimable?.containers?.stopped?.total_bytes)}</span>
                          </div>
                        </div>
                        <div className="container-row-right">
                          {(() => { const c = inst.dockerReport?.reclaimable?.containers?.stopped?.count ?? 0; return (
                            <span className={`pill ${c > 0 ? 'pill--info' : 'pill--muted'}`}>{c}</span>
                          ); })()}
                        </div>
                      </div>
                    </div>
                    <div className="row-tile">
                      <div className="container-row">
                        <div className="container-row-left">
                          <div className="td container-name">Volumes (unused)</div>
                          <div className="td container-meta">
                            <span className="kv-value">{typeof inst.dockerReport?.reclaimable?.volumes?.unused?.bytes === 'number' ? f(inst.dockerReport?.reclaimable?.volumes?.unused?.bytes) : '—'}</span>
                          </div>
                        </div>
                        <div className="container-row-right">
                          {(() => { const c = inst.dockerReport?.reclaimable?.volumes?.unused?.count ?? 0; return (
                            <span className={`pill ${c > 0 ? 'pill--info' : 'pill--muted'}`}>{c}</span>
                          ); })()}
                        </div>
                      </div>
                    </div>
                    <div className="row-tile">
                      <div className="container-row">
                        <div className="container-row-left">
                          <div className="th container-name">TOTAL</div>
                          <div className="td container-meta">
                            <span className="kv-value">{f(inst.dockerReport?.reclaimable?.total_bytes)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Databases (optional) */}
              {(() => {
                try {
                  const doc = inst?.dockerReport;
                  const detail = Array.isArray(doc?.databases_detail) ? doc.databases_detail : null;
                  const map = (doc && doc.databases && typeof doc.databases === 'object') ? doc.databases : null;
                  const fsArr = Array.isArray(doc?.filesystems) ? doc.filesystems : [];
                  const candidates = (fsArr || []).map(fs => ({
                    mount: String(fs?.mount || ''),
                    used: Number(fs?.used_bytes || 0) || 0,
                    size: Number(fs?.size_bytes || 0) || 0,
                    activeVol: Number((fs?.breakdown?.active_volumes_bytes) || 0) || 0
                  }));
                  const primary = primaryMount;
                  const tinyCutoff = 2 * 1024 * 1024 * 1024; // 2GB
                  const assignRows = (rows) => {
                    const out = [];
                    const sorted = rows.slice().sort((a, b) => (Number(b.bytes||0)||0) - (Number(a.bytes||0)||0));
                    for (const r of sorted) {
                      const v = Number(r.bytes || 0) || 0;
                      if (v <= 0) { out.push({ ...r, mount: primary }); continue; }
                      const withActive = candidates.filter(c => c.activeVol > 0);
                      const tolA = Math.max(v * 0.20, 10 * 1024 * 1024 * 1024); // 20% or 10GB
                      const close = withActive.filter(c => Math.abs((c.activeVol || 0) - v) <= tolA);
                      let chosen = null;
                      const pickMinBy = (arr, key) => arr.reduce((best, c) => {
                        const db = Math.abs((best[key] || 0) - v);
                        const dc = Math.abs((c[key] || 0) - v);
                        return dc < db ? c : best;
                      }, arr[0]);
                      if (close.length > 0) {
                        chosen = pickMinBy(close, 'activeVol');
                      }
                      if (!chosen) {
                        const covers = candidates.filter(c => c.used >= v * 0.5 && c.size > tinyCutoff);
                        if (covers.length > 0) {
                          chosen = pickMinBy(covers, 'used');
                        }
                      }
                      if (!chosen) {
                        const avail = candidates.filter(c => c.size > tinyCutoff);
                        chosen = avail[0] || candidates[0] || { mount: primary };
                      }
                      out.push({ ...r, mount: chosen.mount });
                    }
                    return out;
                  };
                  const assign = (rows) => assignRows(rows)
                    .filter(r => String(r.mount || '') === String(currentMount));
                  if (detail && detail.length > 0) {
                    const rows = detail.map(e => ({ bytes: Number(e?.bytes||0)||0, engine: e?.engine, container: e?.container }));
                    const rowsForThis = assign(rows);
                    if (rowsForThis.length === 0) return null;
                    return (
                      <div className="section-panel">
                        <div className="panel-header">Databases</div>
                        <div className="row-list">
                          {rowsForThis.map((e, idx) => (
                            <div key={`${inst.instanceId}-db-${idx}`} className="row-tile">
                              <div className="container-row">
                                <div className="container-row-left">
                                  <div className="td container-name">{(String(e.engine || '') || '').toUpperCase()} — {e.container}</div>
                                  <div className="td container-meta">
                                    <span className="kv-value">{f(e.bytes)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                  if (map && Object.keys(map).length > 0) {
                    const rows = Object.entries(map).map(([k, v]) => ({ key: String(k), bytes: Number(v||0)||0 }));
                    const rowsForThis = assign(rows);
                    if (rowsForThis.length === 0) return null;
                    return (
                      <div className="section-panel">
                        <div className="panel-header">Databases</div>
                        <div className="row-list">
                          {rowsForThis.map((e) => (
                            <div key={`${inst.instanceId}-${e.key}`} className="row-tile">
                              <div className="container-row">
                                <div className="container-row-left">
                                  <div className="td container-name">{e.key}</div>
                                  <div className="td container-meta">
                                    <span className="kv-value">{f(e.bytes)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                } catch {}
                return null;
              })()}
            </>
          ) : (
            <div className="text-muted">No detailed docker report available for this instance.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DiskUsage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [instances, setInstances] = useState([]);
  const [nodeDbInfo, setNodeDbInfo] = useState(null);
  const [dbSizes, setDbSizes] = useState(null);
  const esRef = useRef(null);
  const { getCachedData, setCachedData } = useDataCache();
  const gridRef = useRef(null);
  const [colCount, setColCount] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [selectedEntity, setSelectedEntity] = useState('ALL');
  const [selectedInstance, setSelectedInstance] = useState('__all__');
  const [displayMode, setDisplayMode] = useState('summary');
  const summaryGridApiRef = useRef(null);

  // Smooth gauge wrapper to avoid needle bouncing on rapid updates
  const SmoothGauge = useMemo(() => {
    return function SmoothGaugeInner({ id, value }) {
      return (
        <SvgGauge id={id} value={typeof value === 'number' ? value : 0} width={170} height={105} arcWidth={0.18} minAngle={-95} maxAngle={95} colors={["#008000", "#FFA500", "#DC3545"]} />
      );
    };
  }, []);

  useEffect(() => {
    try { cacheManager.registerEndpoints('disk-usage', ['/api/disk/inventory']); } catch {}

    const applyInventory = (inv) => {
      const list = (inv.instances || []).map(i => ({
        instanceId: i.instanceId,
        entity: i.entity,
        chain: i.chain,
        // host is the friendly label (e.g. FSP MAIN (ENV)) when provided
        host: i.host,
        // ip is the numeric address; fall back to host if inventory doesn't provide a dedicated field
        ip: i.ip || i.ip_address || i.host || null,
        diskSpace: i.diskSpace || null,
        containers: Array.isArray(i.containers) ? i.containers : [],
        breakdown: i.breakdown || null,
        dockerReport: i.dockerReport || null,
        lastUpdated: i.lastUpdated || 0
      }));
      setInstances(list);
    };

    // Hydrate from cache
    try {
      const cached = getCachedData('/api/disk/inventory');
      if (cached && typeof cached === 'object') {
        applyInventory(cached);
        setLoading(false);
      }
    } catch {}

    const load = async () => {
      setError(null);
      try {
        const inv = await fetchDiskInventory();
        setCachedData('/api/disk/inventory', inv);
        applyInventory(inv);
        // fetch db info (shared with Nodes page) and indexer DB sizes in parallel
        try {
          const API = getApiBase();
          const [r1, r2] = await Promise.all([
            fetch(`${API}/node_db_info.json`).catch(()=>null),
            fetch(`${API}/api/disk/db_sizes`).catch(()=>null)
          ]);
          if (r1 && r1.ok) { const db = await r1.json(); if (db && typeof db === 'object') setNodeDbInfo(db); }
          if (r2 && r2.ok) { const sizes = await r2.json(); if (sizes && typeof sizes === 'object') setDbSizes(sizes); }
        } catch {}
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    };
    load();

    // SSE
    try {
      const es = subscribeDiskEvents(
        (inv) => { try { applyInventory(inv); } catch {} },
        (upd) => {
          try {
            setInstances(prev => {
              const next = [...prev];
              const idx = next.findIndex(r => r.instanceId === upd.instanceId);
              const diskSpace = upd.diskSpace || null;
              const lastUpdated = upd.lastUpdated || Date.now();
              if (idx >= 0) next[idx] = { ...next[idx], diskSpace, lastUpdated };
              else next.push({ instanceId: upd.instanceId, entity: '', chain: '', host: '', diskSpace, lastUpdated, containers: [], breakdown: null, dockerReport: null });
              return next;
            });
          } catch {}
        },
        (cupd) => {
          try {
            setInstances(prev => {
              const next = [...prev];
              const idx = next.findIndex(r => r.instanceId === cupd.instanceId);
              const containers = Array.isArray(cupd.containers) ? cupd.containers : [];
              const lastUpdated = cupd.lastUpdated || Date.now();
              if (idx >= 0) next[idx] = { ...next[idx], containers, lastUpdated };
              else next.push({ instanceId: cupd.instanceId, entity: '', chain: '', host: '', diskSpace: null, containers, breakdown: null, dockerReport: null, lastUpdated });
              return next;
            });
          } catch {}
        },
        (bupd) => {
          try {
            setInstances(prev => {
              const next = [...prev];
              const idx = next.findIndex(r => r.instanceId === bupd.instanceId);
              const breakdown = bupd.breakdown || null;
              const lastUpdated = bupd.lastUpdated || Date.now();
              if (idx >= 0) next[idx] = { ...next[idx], breakdown, lastUpdated };
              else next.push({ instanceId: bupd.instanceId, entity: '', chain: '', host: '', diskSpace: null, containers: [], breakdown, dockerReport: null, lastUpdated });
              return next;
            });
          } catch {}
        },
        (rpt) => {
          try {
            setInstances(prev => {
              const next = [...prev];
              const idx = next.findIndex(r => r.instanceId === rpt.instanceId);
              const dockerReport = rpt.report || null;
              const lastUpdated = rpt.lastUpdated || Date.now();
              if (idx >= 0) next[idx] = { ...next[idx], dockerReport, lastUpdated };
              else next.push({ instanceId: rpt.instanceId, entity: '', chain: '', host: '', diskSpace: null, containers: [], breakdown: null, dockerReport, lastUpdated });
              return next;
            });
          } catch {}
        }
      );
      esRef.current = es;
    } catch {}

    return () => {
      try { cacheManager.unregisterEndpoints('disk-usage'); } catch {}
      if (esRef.current) { try { esRef.current.close(); } catch {}; esRef.current = null; }
    };
  }, [getCachedData, setCachedData]);

  // Observe grid to compute number of columns so we can expand per-row
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const compute = () => {
      try {
        const children = Array.from(el.children || []);
        if (children.length === 0) { setColCount(1); return; }
        const firstTop = children[0].offsetTop;
        let cols = 0;
        for (const c of children) {
          if (c.offsetTop !== firstTop) break;
          cols++;
        }
        setColCount(Math.max(1, cols));
      } catch {}
    };
    compute();
    let ro;
    try { ro = new ResizeObserver(compute); ro.observe(el); } catch {}
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    return () => {
      try { ro && ro.disconnect(); } catch {}
      window.removeEventListener('resize', onResize);
    };
  }, []);

  // Recompute columns when instances change (DOM children count changes)
  useEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    try {
      const children = Array.from(el.children || []);
      if (children.length === 0) { setColCount(1); return; }
      const firstTop = children[0].offsetTop;
      let cols = 0;
      for (const c of children) { if (c.offsetTop !== firstTop) break; cols++; }
      setColCount(Math.max(1, cols));
    } catch {}
  }, [instances.length]);

  const computeEntity = (inst) => {
    if (inst && inst.entity) return inst.entity;
    try {
      const id = String(inst?.instanceId || '').toLowerCase();
      const host = String(inst?.host || '').toLowerCase();
      if (id.includes('uys') || host.includes('uys')) return 'UYS';
      if (id.includes('env') || host.includes('env')) return 'ENV';
    } catch {}
    return 'ENV';
  };

  const entityOptions = useMemo(() => {
    const set = new Set(['ALL']);
    try { instances.forEach(i => set.add(computeEntity(i))); } catch {}
    return Array.from(set).map(v => ({ value: v, label: v === 'ALL' ? 'All' : v }));
  }, [instances]);

  const instanceOptions = useMemo(() => {
    const list = Array.from(new Set((instances || []).map(i => i.instanceId))).sort();
    return list.map(v => ({ value: v, label: v }));
  }, [instances]);

  const visibleInstances = useMemo(() => {
    let arr = instances;
    if (selectedEntity && selectedEntity !== 'ALL') {
      arr = arr.filter(i => computeEntity(i) === selectedEntity);
    }
    if (selectedInstance && selectedInstance !== '__all__') {
      arr = arr.filter(i => i.instanceId === selectedInstance);
    }
    return arr;
  }, [instances, selectedEntity, selectedInstance]);

  const summaryRows = useMemo(() => {
    try {
      const expandedEntries = (visibleInstances || []).flatMap((inst) => {
        try {
          const fsArr = Array.isArray(inst?.dockerReport?.filesystems)
            ? inst.dockerReport.filesystems
            : null;
          const primaryMount = inst?.dockerReport?.filesystem?.mount || inst?.diskSpace?.mountpoint || null;
          if (fsArr && fsArr.length > 1) {
            return fsArr.map((fs, idx) => {
              const subInst = { ...inst, dockerReport: { ...inst.dockerReport, filesystem: fs } };
              const isPrimary = primaryMount
                ? String(fs?.mount) === String(primaryMount)
                : idx === 0;
              const hasDeep = !!(fs && fs.breakdown && Object.keys(fs.breakdown || {}).length > 0);
              return { inst: subInst, limited: !isPrimary && !hasDeep ? true : false };
            });
          }
        } catch {}
        return [{ inst, limited: false }];
      });

      const rows = expandedEntries.map(({ inst }) => {
        const dr = inst?.dockerReport || {};
        const fs = dr?.filesystem || {};
        const metrics = computeDiskSummaryMetrics(inst);

        const sizeBytes = Number(fs.size_bytes || 0) || 0;
        const usedBytes = Number(fs.used_bytes || 0) || 0;
        const availBytes = Number(fs.avail_bytes || 0) || 0;

        const disk = fs.mount || inst?.diskSpace?.mountpoint || '/';
        const entity = computeEntity(inst);
        const ip = inst.ip || inst.ip_address || inst.host || null;

        return {
          instanceId: inst.instanceId,
          entity,
          ip,
          // Host column shows the friendly label; prefer instanceId if present
          host: inst.instanceId || inst.host,
          disk,
          size_bytes: sizeBytes,
          used_bytes: usedBytes,
          avail_bytes: availBytes,
          images_total_bytes: metrics.imagesDisplayBytes,
          containers_total_bytes: metrics.containersDisplayBytes,
          other_bytes: metrics.otherDisplayBytes,
          reclaimable_bytes: metrics.reclaimableAligned,
          database_total_bytes: metrics.dbDisplayBytes
        };
      });

      rows.sort((a, b) => {
        const e = String(a.entity || '').localeCompare(String(b.entity || ''));
        if (e !== 0) return e;
        const h = String(a.host || '').localeCompare(String(b.host || ''));
        if (h !== 0) return h;
        return String(a.disk || '').localeCompare(String(b.disk || ''));
      });

      return rows;
    } catch {
      return [];
    }
  }, [visibleInstances]);

  const DiskUsageBarRenderer = (params) => {
    const row = params?.data || {};
    const used = Number(row.used_bytes || 0) || 0;
    const size = Number(row.size_bytes || 0) || 0;
    const pct = size > 0 ? Math.max(0, Math.min(100, (used / size) * 100)) : 0;
    let color = '#22c55e';
    if (pct > 90) {
      color = '#DC3545';
    } else if (pct >= 80) {
      color = '#FFA500';
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
        <div
          style={{
            flex: 1,
            height: 8,
            borderRadius: 999,
            background: 'rgba(31, 41, 55, 0.8)',
            overflow: 'hidden',
            marginRight: 8
          }}
        >
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: color,
              transition: 'width 0.2s ease-out'
            }}
          />
        </div>
        <span style={{ fontSize: 11, color: '#e5e7eb', minWidth: 40, textAlign: 'right' }}>
          {pct.toFixed(0)}%
        </span>
      </div>
    );
  };

  const summaryColumnDefs = useMemo(
    () => [
      {
        field: 'entity',
        headerName: 'Entity',
        minWidth: 100,
        flex: 1,
        cellRenderer: EntityCellRenderer,
        filter: 'agTextColumnFilter'
      },
      {
        field: 'host',
        headerName: 'Host',
        minWidth: 200,
        flex: 1.4,
        filter: 'agTextColumnFilter'
      },
      {
        headerName: 'Usage',
        field: 'usage_pct',
        minWidth: 140,
        flex: 1.2,
        cellRenderer: DiskUsageBarRenderer,
        sortable: false,
        filter: false
      },
      {
        field: 'disk',
        headerName: 'Disk',
        minWidth: 140,
        flex: 1,
        filter: 'agTextColumnFilter',
        valueFormatter: params => formatDiskLabel(params.value)
      },
      {
        field: 'size_bytes',
        headerName: 'Size',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'used_bytes',
        headerName: 'Used',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'avail_bytes',
        headerName: 'Available',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'images_total_bytes',
        headerName: 'Images',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'containers_total_bytes',
        headerName: 'Containers',
        minWidth: 140,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'other_bytes',
        headerName: 'Other',
        minWidth: 130,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'reclaimable_bytes',
        headerName: 'Reclaimable',
        minWidth: 150,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      },
      {
        field: 'database_total_bytes',
        headerName: 'Databases',
        minWidth: 150,
        flex: 1,
        filter: 'agNumberColumnFilter',
        valueFormatter: params => formatBytes(params.value)
      }
    ],
    []
  );

  const summaryDefaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true
    }),
    []
  );

  const handleDisplayModeChange = (mode) => {
    if (mode === displayMode) return;
    setDisplayMode(mode);
  };

  if (loading) return <LoadingAnimation />;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <>
      <div className="disk-page">
        <div className="filters-bar">
          <div className="filters-left">
            <RadioTabs
              name="entity"
              options={entityOptions}
              value={selectedEntity}
              onChange={setSelectedEntity}
            />
          </div>
          <div className="filters-center">
            <div className="disk-toggle-wrapper">
              <div className="disk-network-toggle">
                <button
                  className={`disk-toggle-button ${displayMode === 'summary' ? 'active' : ''}`}
                  onClick={() => handleDisplayModeChange('summary')}
                >
                  SUMMARY
                </button>
                <button
                  className={`disk-toggle-button ${displayMode === 'detail' ? 'active' : ''}`}
                  onClick={() => handleDisplayModeChange('detail')}
                >
                  DETAIL
                </button>
                <div className="disk-slider"></div>
              </div>
            </div>
          </div>
          <div className="filters-right">
            <SelectField
              value={selectedInstance}
              onChange={setSelectedInstance}
              options={instanceOptions}
              placeholder="All Instances"
            />
          </div>
        </div>
        <div className="main-wrapper disk-main">
          {displayMode === 'summary' && (
            <div className="disk-summary-wrapper">
              <div className="ag-theme-alpine-dark custom-dark-theme" style={{ width: '100%', flex: 1, minHeight: 0 }}>
                <AgGridReact
                  columnDefs={summaryColumnDefs}
                  rowData={summaryRows}
                  defaultColDef={summaryDefaultColDef}
                  domLayout="normal"
                  suppressRowClickSelection={true}
                  getRowId={(params) => {
                    const row = params?.data || {};
                    return `${row.instanceId || ''}-${row.disk || ''}`;
                  }}
                  onGridReady={(params) => {
                    summaryGridApiRef.current = params.api;
                    params.api.sizeColumnsToFit();
                  }}
                  onFirstDataRendered={(params) => {
                    params.api.sizeColumnsToFit();
                  }}
                />
              </div>
            </div>
          )}

          {displayMode === 'detail' && (
            <div
              ref={gridRef}
              className="card-grid disk-grid"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, alignItems: 'start' }}
            >
              {visibleInstances
                .flatMap((inst) => {
                  try {
                    const fsArr = Array.isArray(inst?.dockerReport?.filesystems)
                      ? inst.dockerReport.filesystems
                      : null;
                    const primaryMount = inst?.dockerReport?.filesystem?.mount || inst?.diskSpace?.mountpoint || null;
                    if (fsArr && fsArr.length > 1) {
                      return fsArr.map((fs, idx) => {
                        const subInst = { ...inst, dockerReport: { ...inst.dockerReport, filesystem: fs } };
                        const isPrimary = primaryMount
                          ? String(fs?.mount) === String(primaryMount)
                          : idx === 0;
                        const hasDeep = !!(fs && fs.breakdown && Object.keys(fs.breakdown || {}).length > 0);
                        return { inst: subInst, limited: !isPrimary && !hasDeep ? true : false };
                      });
                    }
                  } catch {}
                  return [{ inst, limited: false }];
                })
                .map((entry, index) => {
                  const row = Math.floor(index / Math.max(colCount, 1));
                  const isExpanded = expandedRow === row;
                  const onToggleExpand = () => setExpandedRow((prev) => (prev === row ? null : row));
                  return (
                    <InstanceCard
                      key={`${entry.inst.instanceId}-${index}`}
                      inst={entry.inst}
                      index={index}
                      SmoothGauge={SmoothGauge}
                      isExpanded={isExpanded}
                      onToggleExpand={onToggleExpand}
                      nodeDbInfo={nodeDbInfo}
                      dbSizes={dbSizes}
                      limited={entry.limited}
                    />
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
